/**
 * Medallas de los trofeos — arte entregado por Santiago el 18/09/2026.
 *
 * Fuente: `Sistema_de_recompensas_completo_3.xlsx` (Drive de la AF), columnas
 * I («Tipo de trofeo» = metal) y J («Categoría de trofeo»). Santiago, por
 * WhatsApp el 18/09: «en el documento del drive ya aparece en la columna J cuál
 * es de cada trofeo (categoría y metal)». El metal está en la I y la categoría
 * en la J; juntas dan la medalla.
 *
 * Cada medalla es el símbolo de la categoría (megáfono = comunicación, « ç » =
 * lingüística, diana = especial, libro = académica) con el metal en la
 * insignia de la esquina. La variante «galia» lleva al loro en esa insignia y
 * es, según Santiago, «el ícono de cada categoría».
 *
 * Imágenes: PNG de ~800 KB y ~1270 px optimizados a WebP de 256 px (~20 KB;
 * 12,2 MB → 321 KB en total). Viven en public/assets/trophies/. Workbox solo
 * precachea JS/CSS/HTML, así que no inflan la instalación de la PWA: se cargan
 * al abrir el cofre.
 *
 * Por ahora solo hay arte para las 4 categorías que usa el catálogo. «Cultural»
 * y «DELF» también tienen medallas en Drive, pero sus trofeos (5 culturales y
 * 6 DELF del Excel) todavía no existen en src/config/trophies.ts.
 */

export type MedalCategory = 'comunicacion' | 'linguistica' | 'especial' | 'academica';
export type MedalMetal = 'bronce' | 'plata' | 'oro';

export interface TrophyMedal {
  category: MedalCategory;
  metal: MedalMetal;
}

/** Nombre de la categoría en la interfaz del cofre (que está en francés). */
export const CATEGORY_LABELS_FR: Record<MedalCategory, string> = {
  linguistica: 'Linguistique',
  comunicacion: 'Communication',
  especial: 'Spécial',
  academica: 'Académique',
};

export const METAL_LABELS_FR: Record<MedalMetal, string> = {
  bronce: 'Bronze',
  plata: 'Argent',
  oro: 'Or',
};

/** id del trofeo (trophies.ts) → medalla. Generado del Excel el 18/09/2026. */
export const TROPHY_MEDALS: Record<string, TrophyMedal> = {
  // A1
  a1_saluer: { category: 'comunicacion', metal: 'bronce' },
  a1_etre: { category: 'linguistica', metal: 'bronce' },
  a1_localiser: { category: 'linguistica', metal: 'bronce' },
  a1_compter: { category: 'linguistica', metal: 'bronce' },
  a1_loisirs: { category: 'comunicacion', metal: 'bronce' },
  a1_famille: { category: 'comunicacion', metal: 'bronce' },
  a1_questions: { category: 'linguistica', metal: 'bronce' },
  a1_decouverte: { category: 'especial', metal: 'plata' },
  // A2
  a2_pouvoir_vouloir: { category: 'linguistica', metal: 'bronce' },
  a2_habitudes: { category: 'comunicacion', metal: 'bronce' },
  a2_passe_compose: { category: 'linguistica', metal: 'bronce' },
  a2_partitifs: { category: 'linguistica', metal: 'bronce' },
  a2_loisirs: { category: 'comunicacion', metal: 'bronce' },
  a2_imparfait: { category: 'comunicacion', metal: 'bronce' },
  a2_commerces: { category: 'comunicacion', metal: 'bronce' },
  a2_aventure: { category: 'especial', metal: 'plata' },
  // B1
  b1_conseils: { category: 'comunicacion', metal: 'bronce' },
  b1_voyage: { category: 'comunicacion', metal: 'bronce' },
  b1_conditionnel: { category: 'linguistica', metal: 'bronce' },
  b1_discours: { category: 'comunicacion', metal: 'bronce' },
  b1_subjonctif: { category: 'linguistica', metal: 'bronce' },
  b1_films: { category: 'linguistica', metal: 'bronce' },
  b1_comparaison: { category: 'comunicacion', metal: 'bronce' },
  b1_systeme_d: { category: 'especial', metal: 'plata' },
  // B2
  b2_livre: { category: 'comunicacion', metal: 'bronce' },
  b2_point_de_vue: { category: 'comunicacion', metal: 'bronce' },
  b2_gerondif: { category: 'linguistica', metal: 'bronce' },
  b2_se_faire: { category: 'linguistica', metal: 'bronce' },
  b2_environnement: { category: 'comunicacion', metal: 'bronce' },
  b2_societe: { category: 'comunicacion', metal: 'bronce' },
  b2_conditionnel_passe: { category: 'linguistica', metal: 'bronce' },
  b2_vol_solo: { category: 'especial', metal: 'plata' },
  // C1
  // Columna J VACÍA en el Excel (metal Bronce): Comunicación por coherencia con el resto del nivel C. CONFIRMAR con Santiago.
  c_connecteurs: { category: 'comunicacion', metal: 'bronce' },
  c_humour: { category: 'comunicacion', metal: 'bronce' },
  c_registres: { category: 'comunicacion', metal: 'bronce' },
  c_critique: { category: 'comunicacion', metal: 'bronce' },
  c_reporter: { category: 'comunicacion', metal: 'bronce' },
  c_grammairien: { category: 'comunicacion', metal: 'bronce' },
  c_idiomatiques: { category: 'comunicacion', metal: 'bronce' },
  c_orateur: { category: 'comunicacion', metal: 'bronce' },
  c_citoyen: { category: 'comunicacion', metal: 'bronce' },
  c_globetrotter: { category: 'comunicacion', metal: 'bronce' },
  c_sommet: { category: 'especial', metal: 'plata' },
  // AEC
  aec_co: { category: 'academica', metal: 'bronce' },
  aec_ce: { category: 'academica', metal: 'bronce' },
  aec_po: { category: 'academica', metal: 'bronce' },
  aec_pe: { category: 'academica', metal: 'bronce' },
  aec_sans_faute: { category: 'academica', metal: 'bronce' },
  aec_assiduite: { category: 'academica', metal: 'bronce' },
  // Mesa 29/09/2026 (mejor nota del curso): no está en el Excel. Plata por ser un logro relativo al grupo. CONFIRMAR con Santiago.
  aec_meilleur: { category: 'academica', metal: 'plata' },
  aec_sommet: { category: 'academica', metal: 'plata' },
  aec_trio: { category: 'academica', metal: 'oro' },
  // Kiosco
  // No está en el Excel (trofeo del kiosco, propio de LiDARit). CONFIRMAR con Santiago.
  kiosk_saluer: { category: 'comunicacion', metal: 'bronce' },
  // No está en el Excel (trofeo del kiosco, propio de LiDARit). CONFIRMAR con Santiago.
  kiosk_question: { category: 'comunicacion', metal: 'bronce' },
  // No está en el Excel (trofeo del kiosco, propio de LiDARit). CONFIRMAR con Santiago.
  kiosk_adieu: { category: 'comunicacion', metal: 'bronce' },
};

const BASE = '/assets/trophies';

/** Imagen de la medalla de un trofeo, o null si no tiene (se usa el emoji). */
export function medalSrc(trophyId: string): string | null {
  const m = TROPHY_MEDALS[trophyId];
  return m ? `${BASE}/${m.category}-${m.metal}.webp` : null;
}

/** Ícono de la categoría: la medalla con Galia en la insignia. */
export function categoryIconSrc(category: MedalCategory): string {
  return `${BASE}/${category}-galia.webp`;
}

/** Texto accesible de la medalla: « Communication · Bronze ». */
export function medalLabel(trophyId: string): string | null {
  const m = TROPHY_MEDALS[trophyId];
  return m ? `${CATEGORY_LABELS_FR[m.category]} · ${METAL_LABELS_FR[m.metal]}` : null;
}
